import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { SessionStore } from 'app/core/services/session.store';
import { UserId } from 'app/shared/models/User';
import { LocalStorageService } from 'app/shared/services/localStorage.service';

const storageKey = (userId: UserId, featureId: string) => `feature-onboarding-${userId}-${featureId}`;

/**
 * Remembers which new-feature onboardings the installer has seen, per installer and per browser.
 * Root-provided: the header and the pages read the same state, and it outlives every route.
 */
@Injectable({ providedIn: 'root' })
export class FeatureOnboardingStore {
    private readonly session = inject(SessionStore);
    private readonly storage = inject(LocalStorageService);
    private readonly seenByKey = new Map<string, WritableSignal<boolean>>();
    private readonly seenByFeature = new Map<string, Signal<boolean>>();

    /** `true` while no installer is signed in, so nothing shows before the session is loaded. */
    isSeen(featureId: string): Signal<boolean> {
        let seen = this.seenByFeature.get(featureId);
        if (!seen) {
            seen = computed(() => {
                const user = this.session.user();
                return !user || this.state(storageKey(user.id, featureId))();
            });
            this.seenByFeature.set(featureId, seen);
        }
        return seen;
    }

    markSeen(featureId: string): void {
        const user = this.session.user();
        if (!user) return;

        const key = storageKey(user.id, featureId);
        this.state(key).set(true);
        this.storage.setItem(key, true);
    }

    private state(key: string): WritableSignal<boolean> {
        let state = this.seenByKey.get(key);
        if (!state) {
            state = signal(this.readSeen(key));
            this.seenByKey.set(key, state);
        }
        return state;
    }

    /** The browser storage is user-editable: a value that no longer parses counts as not seen. */
    private readSeen(key: string): boolean {
        try {
            return this.storage.getItem(key) === true;
        } catch {
            return false;
        }
    }
}
